import { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
// The admin area (and its drag-and-drop dependencies) is only needed by
// administrators, so keep it out of the initial bundle.
const AdminPage = lazy(() => import('./pages/AdminPage'));
import { useGetProfileQuery, useLazySilentLoginQuery } from './store/apiSlice';
import { silentOidcRefresh } from './utils/oidc';

const App = () => {
    const {
        data: user,
        isLoading: userLoading,
        error: userError,
        refetch: refetchUser,
    } = useGetProfileQuery();

    const [triggerSilentLogin] = useLazySilentLoginQuery();

    const isUnauthorized =
        !!userError &&
        typeof userError === 'object' &&
        'status' in userError &&
        (userError as any).status === 401;

    const attemptedAutoLoginRef = useRef(false);

    // If we don't have a local session, try to silently restore it.
    // Strategy:
    //  1) backend /rest/Authenticate/silent (refresh_token-based)
    //  2) fallback: iframe-based OIDC prompt=none (reuses IdP session cookie)
    useEffect(() => {
        if (attemptedAutoLoginRef.current) return;
        // Never auto-login from inside the silent-login iframe. If /oidc is ever
        // misrouted to the SPA fallback (e.g. mod_rewrite unavailable), the nested
        // app would otherwise spawn another iframe and recurse without bound.
        if (window.top !== window.self) return;
        // The login page owns interactive retries; errors/logout must not trigger another SSO loop.
        if (window.location.pathname === '/login') return;
        if (userLoading) return;
        if (user) return;
        if (!isUnauthorized) return;

        attemptedAutoLoginRef.current = true;

        let canceled = false;
        (async () => {
            try {
                // 1) server-side refresh_token attempt
                const apiResult = await triggerSilentLogin().unwrap();
                if (canceled || apiResult?.autoLoginBlocked) return;

                if (apiResult?.authenticated) {
                    await refetchUser();
                    return;
                }

                // 2) fallback to IdP silent auth (prompt=none) via hidden iframe
                const oidcResult = await silentOidcRefresh({ redirect: '/' });
                if (canceled) return;

                if (oidcResult.status === 'success') {
                    await refetchUser();
                    return;
                }

                // If the IdP can't do prompt=none (commonly due to 3rd-party cookie restrictions),
                // fall back to an interactive login redirect.
                if (oidcResult.status === 'error' && oidcResult.reason === 'login_required') {
                    const redirectPath = window.location.pathname + window.location.search;
                    window.location.href = `/oidc?redirect=${encodeURIComponent(redirectPath)}`;
                }
            } catch {
                // ignore
            }
        })();

        return () => {
            canceled = true;
        };
    }, [user, userLoading, isUnauthorized, triggerSilentLogin, refetchUser]);

    if (userLoading && !userError) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Suspense
            fallback={
                <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                    <CircularProgress />
                </Stack>
            }
        >
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route
                    path="/login"
                    element={user ? <Navigate to="/" replace /> : <LoginPage />}
                />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
};

export default App;
